import { Loader2Icon } from 'lucide-react'
import { memo, type ReactNode } from 'react'
import type { UseFormReturn } from 'react-hook-form'
import { getAppBaseUrl } from '@/app.const'
import { SquareImageUpload } from '@/components/molecules/square-image-upload.molecule'
import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'

import type { CreateOrganizationFormProps } from './org-settings.interface'
import type { CreateOrganizationFormSchemaType } from './org-settings.schema'
import { SlugSuggestions } from './slug-suggestions.atom'
import { useCreateOrganizationForm } from './use-create-organization-form.hook'
import { useSlugConflict } from './use-slug-conflict.hook'

interface NameFieldProps {
  form: UseFormReturn<CreateOrganizationFormSchemaType>
  isLoading: boolean
  onNameChange: (value: string) => void
}

const NameField: React.FC<NameFieldProps> = ({ form, isLoading, onNameChange }) => (
  <FormField
    control={form.control}
    name="name"
    render={({ field }) => (
      <FormItem>
        <FormLabel>Organization Name</FormLabel>
        <FormControl>
          <Input
            {...field}
            placeholder="My Organization"
            disabled={isLoading}
            className="create-organization-name-input"
            onChange={(e) => onNameChange(e.target.value)}
          />
        </FormControl>
        <FormMessage />
      </FormItem>
    )}
  />
)

interface SlugFieldProps {
  form: UseFormReturn<CreateOrganizationFormSchemaType>
  isLoading: boolean
  currentSlug: string
  onSlugChange: (value: string) => void
  suggestions: string[]
  onSuggestionSelect: (slug: string) => void
}

const SlugField: React.FC<SlugFieldProps> = ({
  form,
  isLoading,
  currentSlug,
  onSlugChange,
  suggestions,
  onSuggestionSelect,
}) => (
  <FormField
    control={form.control}
    name="slug"
    render={({ field }) => (
      <FormItem>
        <FormLabel>Organization URL</FormLabel>
        <FormControl>
          <div className="create-organization-slug-wrapper flex items-center gap-2">
            <span className="text-sm text-muted-foreground whitespace-nowrap">
              {getAppBaseUrl()}/
            </span>
            <Input
              {...field}
              placeholder="my-organization"
              disabled={isLoading}
              className="create-organization-slug-input font-mono lowercase"
              onChange={(e) => onSlugChange(e.target.value)}
            />
          </div>
        </FormControl>
        <FormDescription>
          {currentSlug ? (
            <span className="create-organization-slug-preview font-mono text-xs">
              {getAppBaseUrl()}/{currentSlug}
            </span>
          ) : (
            'This will be your organization URL'
          )}
        </FormDescription>
        <FormMessage />
        {suggestions.length > 0 && (
          <SlugSuggestions suggestions={suggestions} onSelect={onSuggestionSelect} />
        )}
      </FormItem>
    )}
  />
)

interface FormActionsProps {
  isLoading: boolean
  isValid: boolean
  onCancel: () => void
}

const FormActions: React.FC<FormActionsProps> = ({ isLoading, isValid, onCancel }) => (
  <div className="create-organization-actions flex justify-end gap-2">
    <Button
      type="button"
      variant="outline"
      onClick={onCancel}
      disabled={isLoading}
      className="create-organization-cancel-button"
    >
      Cancel
    </Button>
    <Button
      type="submit"
      disabled={isLoading || !isValid}
      className="create-organization-submit-button"
    >
      {isLoading ? (
        <>
          <Loader2Icon className="mr-2 size-4 animate-spin" />
          Creating...
        </>
      ) : (
        'Create Organization'
      )}
    </Button>
  </div>
)

interface LogoUploadProps {
  logoPreview: string | null
  currentName: string
  isLoading: boolean
  onLogoChange: (file: File | null) => void
  onLogoRemove: () => void
}

const LogoUpload: React.FC<LogoUploadProps> = ({
  logoPreview,
  currentName,
  isLoading,
  onLogoChange,
  onLogoRemove,
}) => (
  <div className="create-organization-logo-wrapper shrink-0">
    <SquareImageUpload
      imageUrl={logoPreview}
      fallbackText={currentName || 'ORG'}
      onImageChange={onLogoChange}
      onImageRemove={onLogoRemove}
      isDisabled={isLoading}
      size="lg"
      uploadTooltip="Upload organization logo"
      changeTooltip="Change organization logo"
      removeTooltip="Remove organization logo"
    />
  </div>
)

interface CreateOrgFormFieldsProps {
  formState: ReturnType<typeof useCreateOrganizationForm>
  isLoading: boolean
  suggestions: string[]
  onSuggestionSelect: (slug: string) => void
}

const CreateOrgFormFields: React.FC<CreateOrgFormFieldsProps> = ({
  formState,
  isLoading,
  suggestions,
  onSuggestionSelect,
}) => {
  const { form, handleNameChange, handleSlugChange } = formState
  const currentSlug = form.watch('slug')
  const currentName = form.watch('name')

  return (
    <div className="create-organization-form-content flex flex-col gap-6 sm:flex-row sm:items-start">
      <LogoUpload
        logoPreview={formState.logoPreview}
        currentName={currentName}
        isLoading={isLoading}
        onLogoChange={formState.handleLogoChange}
        onLogoRemove={formState.handleLogoRemove}
      />
      <div className="create-organization-fields flex-1 space-y-4">
        <NameField form={form} isLoading={isLoading} onNameChange={handleNameChange} />
        <SlugField
          form={form}
          isLoading={isLoading}
          currentSlug={currentSlug}
          onSlugChange={handleSlugChange}
          suggestions={suggestions}
          onSuggestionSelect={onSuggestionSelect}
        />
      </div>
    </div>
  )
}

const CreateOrganizationFormBase: React.FC<CreateOrganizationFormProps> = ({
  onSubmit,
  onCancel,
  isLoading = false,
  shouldReset = false,
  className,
}): ReactNode => {
  const formState = useCreateOrganizationForm({ shouldReset })
  const { form, logoFile } = formState
  const { suggestions, handleConflictError, resetSuggestions, selectSuggestion } = useSlugConflict({
    onError: (msg) => form.setError('slug', { message: msg }),
    onClearError: () => form.clearErrors('slug'),
    onApplySlug: formState.handleSlugChange,
  })

  const handleFormSubmit = async (data: CreateOrganizationFormSchemaType): Promise<void> => {
    try {
      resetSuggestions()
      await onSubmit({ ...data, logoFile })
    } catch (error) {
      if (handleConflictError(error)) return
      throw error
    }
  }

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(handleFormSubmit)}
        className={cn('create-organization-form space-y-6', className)}
      >
        <CreateOrgFormFields
          formState={formState}
          isLoading={isLoading}
          suggestions={suggestions}
          onSuggestionSelect={selectSuggestion}
        />
        <FormActions isLoading={isLoading} isValid={form.formState.isValid} onCancel={onCancel} />
      </form>
    </Form>
  )
}

export const CreateOrganizationForm = memo(CreateOrganizationFormBase)

CreateOrganizationForm.displayName = 'CreateOrganizationForm'
